export interface SitePolygon {
    lat: number;
    lng: number;
}

export interface SiteFormData {
    name: string;
    startDate: string;
    mgr: string;
    addr: string;
    area: string;
    pyeong: string;
    treeName: string;
    treeCount: string;
    treePlantDate: string;
    soilType: string;
    ph: string;
    initialMoist: string;
    soilDepth: string;
    drainage: string;
    memo: string;
}

export interface SavedSite {
    id: string;
    polygon: SitePolygon[];
    form: SiteFormData;
    moist: number; // 0-100 (demo random at save time)
    temp?: number;    // °C (demo)
    weather?: WeatherType;
    createdAt: string;
}

export type WeatherType = 'sunny' | 'partly_cloudy' | 'cloudy' | 'rainy' | 'windy';

export const WEATHER_ICON: Record<WeatherType, string> = {
    sunny:         '☀️',
    partly_cloudy: '⛅',
    cloudy:        '☁️',
    rainy:         '🌧️',
    windy:         '💨',
};

export const WEATHER_LABEL: Record<WeatherType, string> = {
    sunny:         '맑음',
    partly_cloudy: '구름 조금',
    cloudy:        '흐림',
    rainy:         '비',
    windy:         '강풍',
};

const WEATHER_TYPES: WeatherType[] = ['sunny', 'partly_cloudy', 'cloudy', 'rainy', 'windy'];

/** 저장 시 weather/temp 없는 구 데이터에 대한 결정론적 폴백 */
export function getSiteWeather(site: SavedSite): { temp: number; weather: WeatherType } {
    if (site.temp !== undefined && site.weather !== undefined) {
        return { temp: site.temp, weather: site.weather };
    }
    const hash = parseInt(site.id.replace(/\D/g, '').slice(-5) || '0', 10);
    return {
        temp:    18 + (hash % 15),
        weather: WEATHER_TYPES[hash % WEATHER_TYPES.length],
    };
}

/* ── Moist status helpers (raw hex — usable inside Naver Maps HTML strings) ── */
export type MoistStatus = 'good' | 'caution' | 'warn' | 'danger';

export function getMoistStatus(moist: number): MoistStatus {
    if (moist >= 70) return 'good';
    if (moist >= 45) return 'caution';
    if (moist >= 25) return 'warn';
    return 'danger';
}

export const MOIST_COLOR: Record<MoistStatus, string> = {
    good: '#1E8B53',
    caution: '#2563EB',
    warn: '#D97706',
    danger: '#DC2626',
};

export const MOIST_FILL: Record<MoistStatus, string> = {
    good: 'rgba(30,139,83,0.13)',
    caution: 'rgba(37,99,235,0.11)',
    warn: 'rgba(217,119,6,0.13)',
    danger: 'rgba(220,38,38,0.13)',
};

export const MOIST_LABEL: Record<MoistStatus, string> = {
    good: '양호',
    caution: '주의',
    warn: '위험',
    danger: '심각',
};

/* ── 수종 이모지 ── */
export function treeEmoji(treeName: string): string {
    if (!treeName) return '🌳';
    if (/벚/.test(treeName))                   return '🌸';
    if (/소나무|잣나무|침엽/.test(treeName))    return '🌲';
    if (/메타세쿼이아|낙우송/.test(treeName))   return '🌲';
    if (/은행/.test(treeName))                 return '🍃';
    if (/버들|수양/.test(treeName))             return '🌿';
    if (/느티|참나무|상수리/.test(treeName))    return '🌳';
    if (/이팝/.test(treeName))                 return '🌼';
    if (/단풍/.test(treeName))                 return '🍁';
    if (/목련/.test(treeName))                 return '🌼';
    return '🌳';
}

/* ── localStorage ── */
const LS_KEY = 'treemap_demo_sites';

// 초기 데모 데이터
const INITIAL_DEMO_SITES: SavedSite[] = [
    {
        id: 'site_1780277944047',
        polygon: [
            { lat: 37.9601257, lng: 127.3051527 },
            { lat: 37.9598973, lng: 127.3050991 },
            { lat: 37.9592883, lng: 127.3055926 },
            { lat: 37.9589837, lng: 127.3055175 },
            { lat: 37.9587469, lng: 127.3055282 },
            { lat: 37.9585439, lng: 127.3055497 },
            { lat: 37.9576049, lng: 127.3070196 },
            { lat: 37.9577064, lng: 127.3075345 },
            { lat: 37.9578502, lng: 127.3075238 },
            { lat: 37.9582224, lng: 127.3070303 },
            { lat: 37.9598973, lng: 127.3063651 },
            { lat: 37.9598297, lng: 127.3061183 },
            { lat: 37.9601596, lng: 127.305539 },
        ],
        form: {
            name: '조경수그룹A',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.958989, 경도 127.306121',
            area: '25,555',
            pyeong: '7,730',
            treeName: '소나무',
            treeCount: '50',
            treePlantDate: '2026-06-01',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '심각',
            memo: '',
        },
        moist: 5,
        createdAt: '2026-06-01T01:39:04.047Z',
    },
    {
        id: 'site_1780278000743',
        polygon: [
            { lat: 37.9580617, lng: 127.3029533 },
            { lat: 37.9579094, lng: 127.3036936 },
            { lat: 37.9579433, lng: 127.3041978 },
            { lat: 37.9571481, lng: 127.3044446 },
            { lat: 37.9571566, lng: 127.3046699 },
            { lat: 37.9568436, lng: 127.3049059 },
            { lat: 37.9565475, lng: 127.304863 },
            { lat: 37.9564714, lng: 127.3038652 },
            { lat: 37.9567928, lng: 127.3017517 },
            { lat: 37.9574611, lng: 127.3018697 },
        ],
        form: {
            name: '가로수A그룹',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.957234, 경도 127.303721',
            area: '34,059',
            pyeong: '10,303',
            treeName: '은행나무',
            treeCount: '50',
            treePlantDate: '2025-03-12',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 58,
        createdAt: '2026-06-01T01:40:00.743Z',
    },
    {
        id: 'site_1780278052955',
        polygon: [
            { lat: 37.9559892, lng: 127.3097983 },
            { lat: 37.9562007, lng: 127.310501 },
            { lat: 37.9559849, lng: 127.3105815 },
            { lat: 37.9556889, lng: 127.3096964 },
            { lat: 37.9558538, lng: 127.3094979 },
        ],
        form: {
            name: '조경수식재단지A',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.955944, 경도 127.310015',
            area: '2,349',
            pyeong: '711',
            treeName: '단풍나무',
            treeCount: '15',
            treePlantDate: '2025-03-20',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 3,
        createdAt: '2026-06-01T01:40:52.955Z',
    },
    {
        id: 'site_1780278152211',
        polygon: [
            { lat: 37.9570381, lng: 127.309353 },
            { lat: 37.9569451, lng: 127.3097393 },
            { lat: 37.9569705, lng: 127.3101631 },
            { lat: 37.9567632, lng: 127.3103186 },
            { lat: 37.9566955, lng: 127.3098627 },
            { lat: 37.956814, lng: 127.3092994 },
        ],
        form: {
            name: '조경수식재단지B',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.956871, 경도 127.309789',
            area: '2,108',
            pyeong: '638',
            treeName: '벚나무',
            treeCount: '22',
            treePlantDate: '2025-04-05',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 86,
        createdAt: '2026-06-01T01:42:32.211Z',
    },
    {
        id: 'site_1780278194870',
        polygon: [
            { lat: 37.9577191, lng: 127.3092511 },
            { lat: 37.9580236, lng: 127.3107853 },
            { lat: 37.9577952, lng: 127.3108604 },
            { lat: 37.9576768, lng: 127.3110643 },
            { lat: 37.9576514, lng: 127.3114612 },
            { lat: 37.9582351, lng: 127.3125663 },
            { lat: 37.9584297, lng: 127.3124698 },
            { lat: 37.9583536, lng: 127.3122337 },
            { lat: 37.958675, lng: 127.3118368 },
            { lat: 37.9587681, lng: 127.3106137 },
            { lat: 37.9588357, lng: 127.3100236 },
            { lat: 37.9590895, lng: 127.309691 },
            { lat: 37.9589965, lng: 127.3095193 },
            { lat: 37.9591064, lng: 127.3089722 },
            { lat: 37.9587173, lng: 127.3087254 },
            { lat: 37.9585227, lng: 127.3088434 },
            { lat: 37.9585904, lng: 127.3093691 },
            { lat: 37.9584043, lng: 127.3102167 },
            { lat: 37.9582267, lng: 127.3102703 },
            { lat: 37.9581505, lng: 127.309176 },
        ],
        form: {
            name: '조경수식재단지C',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.958398, 경도 127.310397',
            area: '27,798',
            pyeong: '8,409',
            treeName: '이팝나무',
            treeCount: '65',
            treePlantDate: '2024-10-15',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 25,
        createdAt: '2026-06-01T01:43:14.870Z',
    },
    {
        id: 'site_1780278240137',
        polygon: [
            { lat: 37.9548387, lng: 127.3058823 },
            { lat: 37.9549994, lng: 127.3061988 },
            { lat: 37.9552532, lng: 127.3067245 },
            { lat: 37.9554266, lng: 127.3073146 },
            { lat: 37.9555746, lng: 127.3079315 },
            { lat: 37.9554689, lng: 127.3089346 },
            { lat: 37.9554393, lng: 127.3093262 },
            { lat: 37.955507, lng: 127.3093584 },
            { lat: 37.955562, lng: 127.3086235 },
            { lat: 37.9556254, lng: 127.3079368 },
            { lat: 37.9554901, lng: 127.3072234 },
            { lat: 37.9553335, lng: 127.3067191 },
            { lat: 37.9551728, lng: 127.3062953 },
            { lat: 37.9549656, lng: 127.3059574 },
        ],
        form: {
            name: '가로수단지 A',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.955333, 경도 127.307459',
            area: '2,568',
            pyeong: '777',
            treeName: '은행나무',
            treeCount: '18',
            treePlantDate: '2025-03-10',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 100,
        createdAt: '2026-06-01T01:44:00.137Z',
    },
    {
        id: 'site_1780278277422',
        polygon: [
            { lat: 37.9538277, lng: 127.3177967 },
            { lat: 37.9534132, lng: 127.3173353 },
            { lat: 37.9528125, lng: 127.3184619 },
            { lat: 37.9534639, lng: 127.3187301 },
        ],
        form: {
            name: '조경수단지C',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.953379, 경도 127.318081',
            area: '7,083',
            pyeong: '2,143',
            treeName: '소나무·잣나무',
            treeCount: '32',
            treePlantDate: '2024-09-20',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 49,
        createdAt: '2026-06-01T01:44:37.422Z',
    },
    {
        id: 'site_1780278299537',
        polygon: [
            { lat: 37.9551474, lng: 127.3153183 },
            { lat: 37.9554097, lng: 127.3145458 },
            { lat: 37.9554097, lng: 127.313591 },
            { lat: 37.9550797, lng: 127.3137197 },
            { lat: 37.9548682, lng: 127.3139879 },
            { lat: 37.9547244, lng: 127.3139128 },
            { lat: 37.9543522, lng: 127.3145029 },
            { lat: 37.9545129, lng: 127.3149643 },
        ],
        form: {
            name: '조경수식재단지D',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.954938, 경도 127.314318',
            area: '11,344',
            pyeong: '3,432',
            treeName: '벚나무·이팝나무',
            treeCount: '45',
            treePlantDate: '2025-04-01',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 86,
        createdAt: '2026-06-01T01:44:59.537Z',
    },
    {
        id: 'site_1780278324202',
        polygon: [
            { lat: 37.954403, lng: 127.310383 },
            { lat: 37.9535654, lng: 127.3103079 },
            { lat: 37.9528717, lng: 127.3101041 },
            { lat: 37.9525756, lng: 127.3098037 },
            { lat: 37.9521526, lng: 127.3094282 },
            { lat: 37.9517466, lng: 127.3094282 },
            { lat: 37.9518904, lng: 127.3101792 },
            { lat: 37.951865, lng: 127.3106084 },
            { lat: 37.9519581, lng: 127.3108444 },
            { lat: 37.952491, lng: 127.3108551 },
            { lat: 37.9528125, lng: 127.3115847 },
            { lat: 37.9525249, lng: 127.3116812 },
            { lat: 37.9526687, lng: 127.3120031 },
            { lat: 37.9529986, lng: 127.3121104 },
            { lat: 37.9533117, lng: 127.3117993 },
            { lat: 37.9535908, lng: 127.3117563 },
            { lat: 37.9538869, lng: 127.3114774 },
            { lat: 37.9539631, lng: 127.3110268 },
        ],
        form: {
            name: '생육필지A',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.952849, 경도 127.310855',
            area: '32,408',
            pyeong: '9,803',
            treeName: '메타세쿼이아',
            treeCount: '80',
            treePlantDate: '2023-11-10',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '심각',
            memo: '',
        },
        moist: 9,
        createdAt: '2026-06-01T01:45:24.202Z',
    },
    {
        id: 'site_1780278342361',
        polygon: [
            { lat: 37.9538023, lng: 127.3074219 },
            { lat: 37.9530579, lng: 127.3073146 },
            { lat: 37.9527364, lng: 127.3069605 },
            { lat: 37.9525249, lng: 127.3070893 },
            { lat: 37.9527279, lng: 127.3075399 },
            { lat: 37.9531509, lng: 127.308012 },
            { lat: 37.9537262, lng: 127.30813 },
        ],
        form: {
            name: '생육필지B',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.953104, 경도 127.307495',
            area: '7,267',
            pyeong: '2,198',
            treeName: '단풍나무·느티나무',
            treeCount: '28',
            treePlantDate: '2025-03-25',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 67,
        createdAt: '2026-06-01T01:45:42.361Z',
    },
    {
        id: 'site_1780278360048',
        polygon: [
            { lat: 37.9536754, lng: 127.3056516 },
            { lat: 37.9525672, lng: 127.3049328 },
            { lat: 37.9521611, lng: 127.3050723 },
            { lat: 37.9522203, lng: 127.3056194 },
            { lat: 37.9528041, lng: 127.3059842 },
            { lat: 37.9531594, lng: 127.3063597 },
            { lat: 37.9533709, lng: 127.3063275 },
            { lat: 37.9536162, lng: 127.3067138 },
            { lat: 37.9534386, lng: 127.306982 },
            { lat: 37.9538362, lng: 127.3071859 },
            { lat: 37.9540054, lng: 127.3064885 },
            { lat: 37.9538277, lng: 127.3064456 },
        ],
        form: {
            name: '생육필지C',
            startDate: '2026-01-15',
            mgr: '김관리자',
            addr: '위도 37.953224, 경도 127.306147',
            area: '15,909',
            pyeong: '4,812',
            treeName: '이팝나무·소나무',
            treeCount: '42',
            treePlantDate: '2024-11-05',
            soilType: '양토',
            ph: '6.5',
            initialMoist: '65',
            soilDepth: '30',
            drainage: '양호',
            memo: '',
        },
        moist: 29,
        createdAt: '2026-06-01T01:46:00.048Z',
    },
];

export function loadSavedSites(): SavedSite[] {
    try {
        const stored = localStorage.getItem(LS_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
        // localStorage에 데이터가 없으면 초기 데모 데이터를 저장하고 반환
        localStorage.setItem(LS_KEY, JSON.stringify(INITIAL_DEMO_SITES));
        return INITIAL_DEMO_SITES;
    } catch {
        // 파싱 에러가 발생하면 초기 데이터 반환
        localStorage.setItem(LS_KEY, JSON.stringify(INITIAL_DEMO_SITES));
        return INITIAL_DEMO_SITES;
    }
}

export function appendSavedSite(
    polygon: SitePolygon[],
    form: SiteFormData,
): SavedSite {
    const moist   = Math.floor(Math.random() * 101);
    const temp    = 15 + Math.floor(Math.random() * 18); // 15-32°C
    const weather = WEATHER_TYPES[Math.floor(Math.random() * WEATHER_TYPES.length)];
    const site: SavedSite = {
        id: `site_${Date.now()}`,
        polygon,
        form,
        moist,
        temp,
        weather,
        createdAt: new Date().toISOString(),
    };
    const list = loadSavedSites();
    list.push(site);
    localStorage.setItem(LS_KEY, JSON.stringify(list));
    return site;
}

export function deleteSavedSiteById(id: string): void {
    const list = loadSavedSites().filter(s => s.id !== id);
    localStorage.setItem(LS_KEY, JSON.stringify(list));
}

/* ── Geometry helpers ── */

/** Shoelace formula — planar approximation, accurate for small areas */
export function calcPolygonAreaM2(pts: SitePolygon[]): number {
    if (pts.length < 3) return 0;
    const refLat = pts[0].lat * (Math.PI / 180);
    const mPerLng = 111_320 * Math.cos(refLat);
    const mPerLat = 111_320;
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const xi = pts[i].lng * mPerLng,
            yi = pts[i].lat * mPerLat;
        const xj = pts[j].lng * mPerLng,
            yj = pts[j].lat * mPerLat;
        area += xi * yj - xj * yi;
    }
    return Math.abs(area / 2);
}

export function calcCentroid(pts: SitePolygon[]): SitePolygon {
    return {
        lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length,
        lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length,
    };
}
